import React from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Clock, Trophy, Home } from 'lucide-react';
import { useRealProgressData } from '@/hooks/useRealProgressData';
import { ALL_COURSE_CATEGORIES } from '@/config/courseNavigation';

import { Badge } from '@/components/ui/badge';

interface UniversalProgressHeaderProps {
  courseType?: string;
  showProgress?: boolean;
}

const UniversalProgressHeader: React.FC<UniversalProgressHeaderProps> = ({ 
  courseType,
  showProgress = true 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { courseId, moduleId } = useParams();
  
  // Detect course type from URL if not provided
  const detectedCourseType = courseType || location.pathname.split('/')[1]?.replace('-courses', '');
  
  // Check if we're on a module page
  const isModulePage = Boolean(moduleId && courseId);
  
  // Get progress data
  const progressData = useRealProgressData(moduleId || "", courseId || "");
  
  // Get course category info
  const category = ALL_COURSE_CATEGORIES.find(cat => cat.id === detectedCourseType);
  const course = category?.courses.find(c => c.id === courseId);

  // Don't show on non-course pages
  if (!detectedCourseType || !courseId) {
    return null;
  }

  const handleBackToCourse = () => {
    const basePath = `/${detectedCourseType}-courses`;
    navigate(`${basePath}/${courseId}`);
  };

  const handleBackToCategory = () => {
    const basePath = `/${detectedCourseType}-courses`;
    navigate(basePath);
  };

  const handleCourseProgressClick = () => {
    if (courseId) {
      const basePath = `/${detectedCourseType}-courses`;
      navigate(`${basePath}/${courseId}`);
    }
  };

  return (
    <div className="sticky top-16 z-30 border-b bg-background">
      <div className="container flex h-14 items-center justify-between px-6">
        {/* Left section - Navigation */}
        <div className="flex items-center space-x-3">
          {isModulePage ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBackToCourse}
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="mr-1 h-3.5 w-3.5" />
              Back to Course
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBackToCategory}
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="mr-1 h-3.5 w-3.5" />
              Back to {category?.title || 'Courses'}
            </Button>
          )}

          {/* Course info - Clickable */}
          {course && (
            <button
              onClick={handleCourseProgressClick}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity group"
            >
              <Home className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary transition-colors" />
              <span className="text-sm font-medium group-hover:text-primary transition-colors">{course.title}</span>
              <Badge variant="outline" className="text-xs">
                {course.level}
              </Badge>
            </button>
          )}
        </div>

        {/* Right section - Progress */}
        {showProgress && isModulePage && !progressData.isLoading && (
          <div className="flex items-center space-x-4">
            {/* Module progress */}
            <div className="flex items-center gap-2">
              <Clock className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Module</span>
              <div className="w-24 bg-secondary/30 rounded-full overflow-hidden">
                <div 
                  className="h-2 bg-blue-500 transition-all duration-300" 
                  style={{ width: `${progressData.moduleProgress}%` }}
                />
              </div>
              <span className="text-xs font-medium">{progressData.moduleProgress}%</span>
            </div>

            {/* Course progress */}
            <div className="flex items-center gap-2">
              <Trophy className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Course</span>
              <div className="w-32 bg-secondary/30 rounded-full overflow-hidden">
                <div 
                  className="h-2 bg-primary transition-all duration-300" 
                  style={{ width: `${progressData.courseProgress}%` }}
                />
              </div>
              <span className="text-xs font-medium">{progressData.courseProgress}%</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UniversalProgressHeader;